import { TFunction } from 'i18next';

/**
 * Standardized error handling utility for consistent error messages across the app
 *
 * Translation keys must use namespace:key format (e.g., "errors:auth.invalidCredentials")
 */
export function handleApiError(
  error: unknown,
  t: TFunction,
  namespace: string = 'common'
): string {
  // Check if it's an API response with error object containing translation key
  if (error && typeof error === 'object' && 'response' in error) {
    const errorWithResponse = error as {
      response?: {
        data?: {
          error?: {
            translationKey?: string;
            translationParams?: Record<string, unknown>;
            message?: string;
            details?: unknown;
          };
        };
      };
    };
    const response = errorWithResponse.response;
    const errorData = response?.data?.error;

    // Special handling for validation errors with details
    if (
      errorData?.translationKey === 'errors:validation.failed' &&
      errorData?.details &&
      Array.isArray(errorData.details)
    ) {
      // Extract the first validation error message
      const firstError = errorData.details[0];
      if (
        firstError &&
        typeof firstError === 'object' &&
        'message' in firstError
      ) {
        const validationMessage = (firstError as { message?: string }).message;
        if (validationMessage) {
          return validationMessage;
        }
      }
    }

    if (errorData?.translationKey) {
      const { translationKey, translationParams } = errorData;

      // Translation keys should already be in namespace:key format
      // If not, prepend the default namespace
      const finalKey = translationKey.includes(':')
        ? translationKey
        : `${namespace}:${translationKey}`;

      return t(finalKey, translationParams || {}) as string;
    }

    // Check if response has a message we can use
    if (errorData?.message) {
      return errorData.message;
    }
  }

  // Check if error object directly has translation key (for some error formats)
  if (error && typeof error === 'object' && 'translationKey' in error) {
    const errorWithTranslation = error as {
      translationKey: string;
      translationParams?: Record<string, unknown>;
    };
    const translationKey = errorWithTranslation.translationKey;
    const translationParams = errorWithTranslation.translationParams || {};

    // Translation keys should already be in namespace:key format
    const finalKey = translationKey.includes(':')
      ? translationKey
      : `${namespace}:${translationKey}`;

    return t(finalKey, translationParams) as string;
  }

  if (error instanceof Error) {
    // Check if the error message is a translation key (format: namespace:key)
    if (error.message.includes(':')) {
      // Try to translate the key
      const translated = t(error.message);
      // If translation exists (not the same as the key), return it
      if (translated !== error.message) {
        return translated;
      }
    }

    // Check if it's a JSON parsing error (HTML error page returned)
    if (
      error.message.includes('Unexpected token') ||
      error.message.includes('<!DOCTYPE')
    ) {
      return t('common:errors.serverError');
    }

    // Check for 404 errors
    if (error.message.includes('404') || error.message.includes('Not Found')) {
      return t('common:errors.notFound');
    }

    // Check for 401/403 errors
    if (error.message.includes('401') || error.message.includes('403')) {
      return t('common:errors.accessDenied');
    }

    // Check for network errors
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return t('common:errors.networkError');
    }

    // Check for unconfirmed user errors from Cognito
    if (
      (error as { code?: string }).code === 'UserNotConfirmedException' ||
      (error as { name?: string }).name === 'UserNotConfirmedException'
    ) {
      return t('auth:errors.userNotConfirmed');
    }

    // Check for passkey-specific errors
    if (error.message === 'NO_PASSKEYS_REGISTERED') {
      return t('auth:errors.noPasskeysRegistered');
    }

    // Return the original error message if it's not a special case
    return error.message;
  }

  // Fallback for non-Error objects
  return t('common:errors.loadFailed');
}

/**
 * Standard error message translations that all pages should have
 */
export const STANDARD_ERROR_KEYS = {
  serverError: 'Server is temporarily unavailable. Please try again later.',
  notFound: 'The requested resource was not found.',
  accessDenied: "You don't have permission to access this resource.",
  networkError: 'Network error. Please check your connection and try again.',
  loadFailed: 'Failed to load data. Please try again.',
};

/**
 * Standard error message translations (Spanish)
 */
export const STANDARD_ERROR_KEYS_ES = {
  serverError:
    'El servidor no está disponible temporalmente. Por favor intenta más tarde.',
  notFound: 'El recurso solicitado no fue encontrado.',
  accessDenied: 'No tienes permiso para acceder a este recurso.',
  networkError:
    'Error de red. Por favor verifica tu conexión e intenta de nuevo.',
  loadFailed: 'Error al cargar los datos. Por favor intenta de nuevo.',
};
